import { useEffect, useRef, useState } from "react";
import type { Speaker, Turn } from "../types";

interface Props {
  transcript: Turn[];
  onSend: (speaker: Speaker, text: string) => void;
  draft: string;
  onDraftChange: (value: string) => void;
  disabled: boolean;
  thinking: boolean;
  autopilot: boolean;
  onAutopilotChange: (value: boolean) => void;
}

/**
 * Customer chat window plus the agent's composer.
 *
 * Both sides are typed here because this is a simulation of a live chat: the "customer"
 * control stands in for the real chat channel so the Copilot's behaviour can be driven
 * end to end without a second participant.
 */
export function ChatWindow({
  transcript,
  onSend,
  draft,
  onDraftChange,
  disabled,
  thinking,
  autopilot,
  onAutopilotChange,
}: Props) {
  const [speaker, setSpeaker] = useState<Speaker>("customer");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcript.length, thinking]);

  const submit = () => {
    const text = draft.trim();
    if (!text || disabled) return;
    onSend(speaker, text);
    onDraftChange("");
  };

  return (
    <section className="chat">
      <header className="chat-header">
        <span>Conversation with the customer</span>
        <label className={`autopilot-toggle ${autopilot ? "on" : ""}`}>
          <input
            type="checkbox"
            checked={autopilot}
            onChange={(event) => onAutopilotChange(event.target.checked)}
          />
          <span className="switch" aria-hidden="true" />
          Autopilot
        </label>
      </header>

      {autopilot ? (
        <div className="autopilot-banner" role="status">
          <strong>Autopilot is on.</strong> Drafted replies go to the customer
          automatically, without your review. Replies from a failed turn are still held back.
        </div>
      ) : null}

      <div className="messages">
        {transcript.length === 0 ? (
          <p className="empty">
            No messages yet. Send something as the customer to start the conversation.
          </p>
        ) : (
          transcript.map((turn) => (
            <div key={turn.id} className={`msg ${turn.speaker}`}>
              <div className="who">
                {turn.speaker === "customer" ? "Customer" : "You"}
                {", "}
                {new Date(turn.ts).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
              {turn.text}
            </div>
          ))
        )}
        {thinking && (
          <div className="msg agent typing" aria-live="polite">
            <div className="who">Copilot is drafting a reply</div>
            <span className="dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <span className="sr-only">The Copilot is drafting a suggested reply.</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="composer">
        <div className="who-toggle" role="group" aria-label="Who is speaking">
          <button
            className={speaker === "customer" ? "active" : ""}
            onClick={() => setSpeaker("customer")}
          >
            Send as customer
          </button>
          <button
            className={speaker === "agent" ? "active" : ""}
            onClick={() => setSpeaker("agent")}
          >
            Send as agent
          </button>
        </div>
        <div className="row">
          <textarea
            value={draft}
            placeholder={
              speaker === "customer"
                ? "Type what the customer says…"
                : "Type your reply to the customer…"
            }
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
          />
          <button className="send" onClick={submit} disabled={disabled || !draft.trim()}>
            Send
          </button>
        </div>
        <span className="hint">
          Enter sends, Shift+Enter starts a new line. What you send is read by the Copilot
          as context, but does not prompt a new suggestion.
          {autopilot
            ? " Autopilot is replying for you - anything you send as the agent is sent in addition to its reply."
            : ""}
        </span>
      </div>
    </section>
  );
}
